/**
 * Recurrence Fallback Generation — client-side background task.
 *
 * Runs once per app-start and once per day to ensure FIXED-schedule recurring
 * instances exist even if the CF DO missed generation (outage, new user, etc.).
 *
 * Uses a 7-day lookahead (DO uses 30 days). Deterministic IDs ensure no duplicates
 * regardless of who generates first.
 *
 * Triggers:
 *   - `app-start` — catch up after device was offline
 *   - `periodic` (6h) — handle multi-day sessions without restart
 */

import { db } from "@/db/schema"
import { generateFixedInstances } from "@totodo/operations"
import { ensureSharedInstancesHaveStartAtIndex } from "./ensureSharedInstances"
import type { BackgroundTaskDef } from "@/background/types"

// 7-day lookahead on client (DO covers 30 days — this is just a safety net)
const CLIENT_LOOKAHEAD_DAYS = 7

// Debounce: don't re-run if less than 6h since last run
const MIN_INTERVAL_MS = 6 * 60 * 60 * 1000
const LAST_GEN_KEY = "#recurrence-last-gen"

export const recurrenceFallbackTask: BackgroundTaskDef = {
  id: "recurrence-fallback",
  description: "Client-side fallback generation for FIXED recurring tasks (7-day lookahead)",
  priority: 8, // After day-change (6), before telemetry (11+)
  triggers: ["app-start", "periodic"],
  intervalMs: MIN_INTERVAL_MS,
  initialDelayMs: 5000, // 5s — let DB + sync settle

  async run(_signal) {
    // Check if enough time has passed
    const lastGen = await getLastGenTime()
    if (lastGen && Date.now() - lastGen < MIN_INTERVAL_MS) return

    // Quick check: does this user have any FIXED recurrences?
    const hasRecurrences = await db.recurrence
      .where("mode")
      .equals("FIXED")
      .filter(r => !r.pausedAt && !r.isDeleted)
      .first()

    if (!hasRecurrences) return

    // Get all FIXED recurrences and generate
    const recurrences = await db.recurrence
      .where("mode")
      .equals("FIXED")
      .filter(r => !r.pausedAt && !r.isDeleted)
      .toArray()

    let totalCreated = 0
    for (const rec of recurrences) {
      try {
        const result = await generateFixedInstances(rec.id, CLIENT_LOOKAHEAD_DAYS)
        if (result.ok && result.data) {
          totalCreated += result.data.created
        }
      } catch (e) {
        console.warn(`[recurrence-fallback] generation failed for ${rec.id}:`, e)
      }
    }

    if (totalCreated > 0) {
      console.info(`[recurrence-fallback] Created ${totalCreated} instances (7-day lookahead)`)
    }

    // Ensure shared instances have local startAtIndex entries
    await ensureSharedInstancesHaveStartAtIndex()

    await setLastGenTime(Date.now())
  },
}

// ---------------------------------------------------------------------------
// Helpers — persist last-gen timestamp in local settings
// ---------------------------------------------------------------------------

async function getLastGenTime(): Promise<number | null> {
  try {
    const entry = await db.table("setting_local").get(LAST_GEN_KEY)
    return entry?.value ? Number(entry.value) : null
  } catch {
    return null
  }
}

async function setLastGenTime(ms: number): Promise<void> {
  try {
    await db.table("setting_local").put({
      id: LAST_GEN_KEY,
      value: String(ms),
      device: "local",
      createdAt: new Date().toISOString(),
      createdBy: "system",
    })
  } catch {
    // Non-critical — next app-start will just re-run
  }
}
